import React, { useState } from 'react';
import {
  Upload, FileCheck2, AlertTriangle, ShieldCheck, ShieldAlert, ShieldX,
  Loader2, Copy, Check, Info, Terminal, Car, Cpu,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// VW brand cue — Volkswagen blue for the tab
const VW_BLUE = '#001E50';

const VERDICT_THEME = {
  SAFE_TO_FLASH:  { icon: ShieldCheck, bg: 'from-emerald-950/30 to-[#0a0a0a]', border: 'border-emerald-500/50', text: 'text-emerald-300', label: 'SAFE TO FLASH' },
  NEEDS_ATTENTION:{ icon: ShieldAlert, bg: 'from-amber-950/30 to-[#0a0a0a]', border: 'border-amber-500/50', text: 'text-amber-300', label: 'NEEDS ATTENTION' },
  DO_NOT_FLASH:   { icon: ShieldX, bg: 'from-red-950/30 to-[#0a0a0a]', border: 'border-red-500/50', text: 'text-red-400', label: 'DO NOT FLASH' },
  UNKNOWN:        { icon: ShieldAlert, bg: 'from-zinc-800/40 to-[#0a0a0a]', border: 'border-zinc-500/50', text: 'text-zinc-300', label: 'UNKNOWN' },
};

const STATUS_DOT = { green: 'bg-emerald-400', yellow: 'bg-amber-400', red: 'bg-red-500 animate-pulse' };

function CopyableCommand({ cmd, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(cmd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };
  return (
    <div className="relative bg-[#050505] border border-[#1f1f1f] rounded-sm px-3 py-2 font-['JetBrains_Mono'] text-[11px] text-emerald-300 overflow-x-auto" data-testid={testId}>
      <pre className="whitespace-pre-wrap break-all pr-8">{cmd}</pre>
      <button
        onClick={copy}
        className="absolute top-1.5 right-1.5 p-1 rounded-sm hover:bg-zinc-800 transition-colors"
        title="Copy"
      >
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-zinc-500" />}
      </button>
    </div>
  );
}

export default function VWFlashPrep() {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);

  const handleFile = (f) => {
    setError(null);
    if (!f) return;
    const nm = (f.name || '').toLowerCase();
    if (!nm.endsWith('.bin') && !nm.endsWith('.frf')) {
      setError('VW file must be .bin or .frf');
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setError(`File exceeds 8 MB (${(f.size / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    setFile(f);
  };

  const run = async () => {
    if (!file) return;
    setSubmitting(true);
    setError(null);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append('vw_bin', file);
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/vw/analyze`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j.detail) msg = j.detail; } catch (_) { /* keep default */ }
        throw new Error(msg);
      }
      const data = await res.json();
      setReport(data);
    } catch (e) {
      setError(e.message || 'Analysis failed');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setFile(null); setReport(null); setError(null); };

  const theme = report ? (VERDICT_THEME[report.verdict] || VERDICT_THEME.UNKNOWN) : null;

  return (
    <div className="max-w-3xl mx-auto" data-testid="vw-flash-prep">
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Car className="w-4 h-4" style={{ color: VW_BLUE }} />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#4dabf7' }}>VW Flash Prep</span>
        </div>
        <h2 className="text-2xl font-bold text-white font-['Outfit']">Pre-flash safety check for your VW tune</h2>
        <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
          Upload your ECU dump. We identify the ECU family (Simos18, MED17.5, DQ250, DQ381...), run safety
          checks, and give you the exact <span className="text-emerald-300 font-['JetBrains_Mono']">VW_Flash</span>{' '}
          command to run locally with RLink, OpenPort2, or Macchina M2.
        </p>
      </div>

      <div className="mb-5 bg-gradient-to-r from-amber-950/40 to-red-950/30 border border-amber-500/40 rounded-sm px-4 py-2.5 flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] leading-relaxed">
          <p className="text-amber-200 font-bold uppercase tracking-wider text-[10px]">
            Actual flashing happens on YOUR laptop, not here.
          </p>
          <p className="text-amber-100/80">
            USB CAN hardware (RLink, OpenPort2) can&apos;t reach a web server. This tool validates the
            file and gives you the right command — you run it locally with{' '}
            <a href="https://github.com/bri3d/VW_Flash" target="_blank" rel="noreferrer" className="underline text-amber-300">bri3d/VW_Flash</a>.
          </p>
        </div>
      </div>

      {!report && (
        <>
          <label
            className={`block border-2 border-dashed rounded-sm p-6 cursor-pointer transition-all ${
              error ? 'border-red-500/50 bg-red-500/5' :
              file ? 'border-emerald-500/50 bg-emerald-500/5' :
              drag ? 'border-blue-500 bg-blue-500/10 scale-[1.01]' :
              'border-zinc-700 hover:border-blue-500/60 hover:bg-blue-500/[0.03] bg-[#0A0A0A]'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
            data-testid="vw-upload"
          >
            <input type="file" accept=".bin,.frf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="vw-upload-input" />
            {file ? (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                  <FileCheck2 className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-['JetBrains_Mono'] text-sm truncate" data-testid="vw-upload-name">{file.name}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{(file.size / 1024 / 1024).toFixed(2)} MB · click or drop to change</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm border border-blue-500/40 bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Cpu className="w-6 h-6 text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm">Drop your VW ECU dump here</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">.bin or .frf · 8 MB max · Simos18 / MED17.5 / DQ250 / DQ381</p>
                </div>
                <Upload className="w-5 h-5 text-zinc-500 flex-shrink-0" />
              </div>
            )}
          </label>

          {error && (
            <div className="mt-3 bg-red-500/10 border border-red-500/40 rounded-sm p-3 text-xs text-red-300 flex items-center gap-2" data-testid="vw-error">
              <AlertTriangle className="w-4 h-4" />{error}
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              onClick={run}
              disabled={!file || submitting}
              className={`px-6 py-3 rounded-sm font-bold text-xs uppercase tracking-wider inline-flex items-center gap-2 transition-all ${
                file && !submitting
                  ? 'bg-blue-500 hover:bg-blue-400 text-white shadow-[0_0_28px_rgba(59,130,246,0.4)]'
                  : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              }`}
              data-testid="vw-run"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {submitting ? 'Analyzing…' : 'Analyze & Get Flash Command'}
            </button>
          </div>
        </>
      )}

      {report && (
        <div className="space-y-4" data-testid="vw-report">
          {/* Verdict panel */}
          <div className={`bg-gradient-to-br ${theme.bg} border-2 ${theme.border} rounded-sm p-5`}>
            <div className="flex items-center gap-3 mb-3">
              <theme.icon className={`w-8 h-8 ${theme.text} flex-shrink-0`} />
              <div className="min-w-0 flex-1">
                <span className={`text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm bg-black/40 ${theme.text}`} data-testid="vw-verdict">
                  {theme.label}
                </span>
                <p className="text-lg font-bold text-white mt-1">
                  {report.ecu_display || 'Unknown ECU'}
                  <span className="text-[10px] text-zinc-500 font-normal ml-2 uppercase">
                    {report.confidence} confidence
                  </span>
                </p>
              </div>
            </div>
            <ul className="text-xs text-zinc-300 space-y-1 mt-2">
              {report.verdict_reasoning?.map((r, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className={theme.text}>›</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Vehicle hints + family notes */}
          {report.ecu_family && (
            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Car className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Likely vehicles</span>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                {report.vehicle_hints?.join(' · ') || 'No specific hints.'}
              </p>
              <div className="mt-3 pt-3 border-t border-[#1a1a1a] grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                <div><span className="text-zinc-500">Flash format:</span> <span className="text-white font-['JetBrains_Mono']">{report.flash_format}</span></div>
                <div><span className="text-zinc-500">Signed:</span> <span className="text-white font-['JetBrains_Mono']">{report.signed ? 'yes (RSA)' : 'no'}</span></div>
                <div><span className="text-zinc-500">Encryption:</span> <span className="text-white font-['JetBrains_Mono']">{report.encryption}</span></div>
              </div>
              <p className="mt-3 text-[11px] text-zinc-400 leading-relaxed italic">
                {report.family_notes}
              </p>
            </div>
          )}

          {/* Safety checks */}
          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Safety checks</span>
            </div>
            <div className="space-y-1.5">
              {report.safety_checks?.map((c) => (
                <div key={c.id} className="flex items-start gap-2 text-[11px]" data-testid={`vw-check-${c.id}`}>
                  <span className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${STATUS_DOT[c.status]}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-white font-semibold">{c.label}</span>
                      <span className="text-zinc-400 font-['JetBrains_Mono'] text-[10px]">{c.value}</span>
                    </div>
                    <p className="text-zinc-500 text-[10px] mt-0.5">{c.note}</p>
                  </div>
                </div>
              ))}
            </div>
            {report.warnings?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#1a1a1a] space-y-1.5">
                {report.warnings.map((w) => (
                  <p key={w.slice(0, 20)} className="text-[11px] text-amber-300 flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Compatible interfaces + commands */}
          {report.compatible_interfaces?.length > 0 && (
            <div className="bg-gradient-to-br from-emerald-950/20 to-[#0a0a0a] border border-emerald-500/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Terminal className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-400">Compatible interfaces · copy the command to run locally</span>
              </div>
              <div className="space-y-3">
                {report.compatible_interfaces.map((i) => (
                  <div key={i.id} data-testid={`vw-iface-${i.id}`}>
                    <div className="flex items-baseline justify-between mb-1 gap-2">
                      <span className="text-white text-sm font-semibold">{i.name}</span>
                      <span className="text-[10px] text-zinc-500">
                        ~${i.cost_usd} · {i.reliability} reliability
                      </span>
                    </div>
                    <CopyableCommand cmd={i.command} testId={`vw-cmd-${i.id}`} />
                    {i.notes && <p className="text-[10px] text-zinc-500 mt-1 italic">{i.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Read-only / incompatible interfaces */}
          {report.unsupported_interfaces?.length > 0 && (
            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Not for flashing this ECU</span>
              </div>
              <div className="space-y-2">
                {report.unsupported_interfaces.map((i) => (
                  <div key={i.id} className="text-[11px]">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-zinc-300 font-semibold">{i.name}</span>
                      <span className="text-[9px] text-zinc-500 uppercase">{i.reliability}</span>
                    </div>
                    <p className="text-zinc-500 text-[10px] mt-0.5">{i.notes}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={reset}
            className="text-xs text-zinc-500 hover:text-white transition-colors"
            data-testid="vw-reset"
          >
            ← Analyze another file
          </button>
        </div>
      )}
    </div>
  );
}
